import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronDown, Download, FileText, Loader2, LogOut, Lock } from "lucide-react";
import RobotMascot from "../components/RobotMascot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => "$" + Math.round(Number(v || 0)).toLocaleString("en-US");

const apiFetch = async (path, opts = {}) => {
  const res = await fetch(`${API}${path}`, { credentials: "include", ...opts });
  if (res.status === 401) {
    const err = new Error("unauthorized");
    err.status = 401;
    throw err;
  }
  return res;
};

const STATUS = {
  approved: { label: "Approved — awaiting signature", cls: "bg-[#007AFF]/10 text-[#007AFF] border-[#007AFF]/25" },
  contracted: { label: "Contracted — funded", cls: "bg-[#14D8A5]/15 text-[#0E7C5E] border-[#14D8A5]/40" },
};

const TABS = ["all", "approved", "contracted"];

const DocLink = ({ reference, category, idx, filename }) => (
  <a
    data-testid={`admin-doc-${reference}-${category}-${idx}`}
    href={`${API}/bridge/${reference}/file/${category}/${idx}`}
    target="_blank"
    rel="noreferrer"
    className="group inline-flex max-w-full items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-medium text-[#0A1628] transition-all duration-200 hover:border-[#007AFF]/50 hover:text-[#007AFF]"
  >
    <Download className="w-3.5 h-3.5 shrink-0" />
    <span className="truncate">{filename}</span>
  </a>
);

const DealCard = ({ deal, open, onToggle }) => {
  const st = STATUS[deal.status] || STATUS.approved;
  const uploads = deal.uploads || {};
  const uw = deal.underwriting || {};
  return (
    <div data-testid={`admin-deal-${deal.reference}`} className="rounded-3xl border border-black/5 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.04)] overflow-hidden">
      <button
        type="button"
        data-testid={`admin-deal-toggle-${deal.reference}`}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 p-6 md:p-7 text-left"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <span className="mono text-xs text-[#52525B]">{deal.reference}</span>
            <span className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${st.cls}`}>{st.label}</span>
          </div>
          <p className="font-display text-xl md:text-2xl font-bold text-[#0A1628] mt-2 truncate">{deal.business_name}</p>
          <p className="mono text-[11px] text-[#52525B] mt-1">{deal.email} · {deal.phone} · {deal.time_in_business || "tenure n/a"}</p>
        </div>
        <div className="flex items-center gap-6 shrink-0">
          <div className="text-right hidden sm:block">
            <p className="font-display text-2xl font-bold text-[#0A1628]">{fmt(deal.taken_amount || deal.approved_amount)}</p>
            <p className="label-mono text-[#52525B] mt-1">{fmt(deal.daily_payment)}/day · {deal.term_days}d</p>
          </div>
          <ChevronDown className={`w-5 h-5 text-[#52525B] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden border-t border-black/5 bg-[#F6F9F8]"
        >
          <div className="p-6 md:p-7 grid md:grid-cols-2 gap-8">
            <div>
              <p className="label-mono text-[#007AFF] mb-4">Terms & underwriting</p>
              <dl className="space-y-2.5 text-sm">
                {[
                  ["Advance", fmt(deal.taken_amount || deal.approved_amount)],
                  ["Payback total", fmt(deal.payback_amount)],
                  ["Daily ACH", `${fmt(deal.daily_payment)} × ${deal.term_days} days`],
                  ["Rate", `${deal.daily_rate_pct}% per day`],
                  ["Read revenue", uw.avg_monthly_deposits ? `${fmt(uw.avg_monthly_deposits)}/mo from statements` : "statements unreadable — tier estimate"],
                  ["Signed by", deal.signature || "— not yet signed"],
                ].map(([k, v], i) => (
                  <div key={i} className="flex justify-between gap-6 border-b border-black/5 pb-2.5">
                    <dt className="text-[#52525B] shrink-0">{k}</dt>
                    <dd data-testid={`admin-${deal.reference}-row-${i}`} className="mono text-[#0A1628] text-right">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="mono text-[11px] text-[#52525B] mt-4">Received {new Date(deal.created_at).toLocaleString("en-US")}</p>
            </div>

            <div className="space-y-5">
              <div>
                <p className="label-mono text-[#007AFF] mb-3">Bank statements ({(deal.statements || []).length})</p>
                <div className="flex flex-wrap gap-2">
                  {(deal.statements || []).map((f, i) => (
                    <DocLink key={i} reference={deal.reference} category="statements" idx={i} filename={f.filename} />
                  ))}
                  {!(deal.statements || []).length && <p className="mono text-xs text-[#52525B]">none</p>}
                </div>
              </div>
              <div>
                <p className="label-mono text-[#007AFF] mb-3">Verification documents</p>
                <div className="flex flex-wrap gap-2">
                  {uploads.license && <DocLink reference={deal.reference} category="license" idx={0} filename={uploads.license.filename} />}
                  {uploads.voided_check && <DocLink reference={deal.reference} category="voided_check" idx={0} filename={uploads.voided_check.filename} />}
                  {uploads.ar && <DocLink reference={deal.reference} category="ar" idx={0} filename={uploads.ar.filename} />}
                  {(uploads.cc_statements || []).map((f, i) => (
                    <DocLink key={i} reference={deal.reference} category="cc_statements" idx={i} filename={f.filename} />
                  ))}
                  {!uploads.license && !uploads.voided_check && !uploads.ar && !(uploads.cc_statements || []).length && (
                    <p className="mono text-xs text-[#52525B]">none uploaded</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

const LoginGate = ({ onAuthed }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Invalid email or password");
      onAuthed(data);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login" className="mt-12 max-w-md mx-auto">
      <form onSubmit={submit} className="rounded-[2rem] border border-black/5 bg-white/85 backdrop-blur-xl p-8 md:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.05)] text-center">
        <span className="lr-float inline-block"><RobotMascot className="w-20" /></span>
        <p className="label-mono text-[#007AFF] mt-4 flex items-center justify-center gap-2">
          <Lock className="w-3.5 h-3.5" /> Manager access only
        </p>
        <h2 className="font-display text-2xl font-bold text-[#0A1628] mt-2">Sign in to review deals</h2>
        <div className="mt-7 space-y-4 text-left">
          <div className="flex flex-col">
            <label className="label-mono text-[#52525B] mb-2">Email</label>
            <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="manager@lendrobot.com" required
              className="w-full rounded-xl border border-black/10 bg-white px-4 py-3.5 text-sm text-[#0A1628] placeholder-black/30 outline-none focus:border-[#007AFF] focus:shadow-[0_0_0_4px_rgba(0,122,255,0.12)] transition-all duration-200" />
          </div>
          <div className="flex flex-col">
            <label className="label-mono text-[#52525B] mb-2">Password</label>
            <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••" required
              className="w-full rounded-xl border border-black/10 bg-white px-4 py-3.5 text-sm text-[#0A1628] placeholder-black/30 outline-none focus:border-[#007AFF] focus:shadow-[0_0_0_4px_rgba(0,122,255,0.12)] transition-all duration-200" />
          </div>
          {error && <p data-testid="admin-login-error" className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-xs font-medium text-red-600">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full rounded-full bg-[#0A1628] py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-[#007AFF] disabled:opacity-60 disabled:pointer-events-none inline-flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Checking…</> : "Sign in"}
          </button>
        </div>
      </form>
    </div>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [auth, setAuth] = useState(null);
  const [user, setUser] = useState(null);
  const [deals, setDeals] = useState(null);
  const [tab, setTab] = useState("all");
  const [openRef, setOpenRef] = useState(null);
  const [error, setError] = useState("");

  const loadDeals = async () => {
    setError("");
    try {
      const res = await apiFetch("/bridge");
      const d = await res.json();
      setDeals(Array.isArray(d) ? d : []);
    } catch (err) {
      if (err.status === 401) {
        try {
          const r = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
          if (!r.ok) throw new Error("expired");
          const res2 = await apiFetch("/bridge");
          const d2 = await res2.json();
          setDeals(Array.isArray(d2) ? d2 : []);
        } catch {
          setAuth(false);
        }
      } else {
        setError("Could not load deals — is the backend awake?");
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/auth/me");
        setUser(await res.json());
        setAuth(true);
      } catch {
        try {
          const r = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
          if (!r.ok) throw new Error("no session");
          const res2 = await apiFetch("/auth/me");
          setUser(await res2.json());
          setAuth(true);
        } catch {
          setAuth(false);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (auth) loadDeals();
  }, [auth]);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
    setDeals(null);
    setAuth(false);
  };

  const filtered = useMemo(() => {
    if (!deals) return [];
    return tab === "all" ? deals : deals.filter((d) => d.status === tab);
  }, [deals, tab]);

  const stats = useMemo(() => {
    if (!deals) return null;
    const contracted = deals.filter((d) => d.status === "contracted");
    return {
      total: deals.length,
      awaiting: deals.length - contracted.length,
      contracted: contracted.length,
      volume: contracted.reduce((s, d) => s + Number(d.taken_amount || 0), 0),
    };
  }, [deals]);

  return (
    <div className="relative min-h-screen pb-24 overflow-hidden">
      <div className="absolute inset-0 bg-grid bg-grid-fade" aria-hidden="true" />
      <div className="absolute top-0 right-[-15%] w-[36rem] h-[36rem] rounded-full bg-[#00E5FF]/15 blur-[130px]" aria-hidden="true" />

      <div className="relative mx-auto max-w-5xl px-5 md:px-8 pt-24 md:pt-28">
        <button
          data-testid="admin-exit"
          onClick={() => navigate("/")}
          className="group inline-flex items-center gap-2 text-sm font-medium text-[#52525B] hover:text-[#0A1628] transition-colors"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /> lendrobot.com
        </button>

        <div className="mt-6 flex items-start justify-between gap-6">
          <div>
            <p className="label-mono text-[#007AFF] mb-3">ADMIN · MANAGER ACCESS</p>
            <h1 className="font-display font-bold tracking-[-0.02em] leading-[1.02] text-[#0A1628] text-3xl md:text-5xl">
              Open bridge deals
            </h1>
            <p className="mt-4 max-w-md text-sm md:text-base leading-relaxed text-[#52525B]">
              Every advance, its terms, statements, and verification documents — in one dashboard.
            </p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <span className="lr-float-slow hidden sm:block shrink-0">
              <RobotMascot className="w-16" />
            </span>
            {auth && user && (
              <button
                data-testid="admin-logout"
                onClick={logout}
                className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-xs font-semibold text-[#52525B] hover:border-black/25 transition-all"
              >
                <LogOut className="w-3.5 h-3.5" /> {user.email}
              </button>
            )}
          </div>
        </div>

        {error && (
          <p data-testid="admin-error" className="mt-8 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-xs font-medium text-red-600">{error}</p>
        )}

        {auth === null && (
          <div className="mt-14 flex items-center justify-center gap-3 text-[#52525B]">
            <Loader2 className="w-5 h-5 animate-spin" /> <span className="mono text-sm">Checking the robot's badge…</span>
          </div>
        )}

        {auth === false && <LoginGate onAuthed={(u) => { setUser(u); setAuth(true); }} />}

        {auth === true && deals && stats && (
          <>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                ["Total deals", stats.total, "admin-stat-total"],
                ["Awaiting signature", stats.awaiting, "admin-stat-awaiting"],
                ["Contracted", stats.contracted, "admin-stat-contracted"],
                ["Funded volume", fmt(stats.volume), "admin-stat-volume"],
              ].map(([label, v, tid]) => (
                <div key={label} data-testid={tid} className="rounded-2xl border border-black/5 bg-white p-5 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
                  <p className="font-display text-2xl md:text-3xl font-bold text-[#0A1628]">{v}</p>
                  <p className="label-mono text-[#52525B] mt-1.5">{label}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex gap-2">
              {TABS.map((t) => (
                <button
                  key={t}
                  data-testid={`admin-tab-${t}`}
                  onClick={() => setTab(t)}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold capitalize transition-all duration-300 ${
                    tab === t
                      ? "bg-[#0A1628] text-white shadow-[0_6px_20px_rgba(10,22,40,0.2)]"
                      : "border border-black/10 text-[#52525B] hover:border-black/25"
                  }`}
                >
                  {t} {t === "all" ? `(${deals.length})` : `(${deals.filter((d) => d.status === t).length})`}
                </button>
              ))}
            </div>

            <div className="mt-6 space-y-4">
              {filtered.map((deal) => (
                <DealCard
                  key={deal.reference}
                  deal={deal}
                  open={openRef === deal.reference}
                  onToggle={() => setOpenRef(openRef === deal.reference ? null : deal.reference)}
                />
              ))}
              {!filtered.length && (
                <div data-testid="admin-empty" className="rounded-3xl border border-dashed border-black/15 bg-white/60 p-14 text-center">
                  <FileText className="w-8 h-8 mx-auto text-[#52525B]" />
                  <p className="font-display text-lg font-semibold text-[#0A1628] mt-4">No {tab === "all" ? "" : tab} deals yet.</p>
                  <p className="mono text-xs text-[#52525B] mt-2">New bridge advances land here the moment they're approved.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
